package com.nonopn.skydome.common.app.pages.pdf

// Output language for generated PDF documents.
// Defaults to the app locale but the user can override it at export time.
enum class PdfOutputLang(val code: String, val displayName: String) {
    FR("fr", "Français"),
    EN("en", "English"),
    ES("es", "Español"),
    PT("pt", "Português");

    companion object {
        fun fromCode(code: String): PdfOutputLang =
            entries.firstOrNull { it.code == code.replace('_', '-').substringBefore('-').lowercase() } ?: EN
    }
}

// All user-visible strings needed inside the generated HTML, per language.
data class PdfStrings(
    val docTitleKeraunic: String,
    val docTitleNsg: String,
    val docTitleLightning: String,
    val site: String,
    val latitude: String,
    val longitude: String,
    val period: String,
    val from: String,
    val to: String,
    val totalStrikes: String,
    val maxIntensity: String,
    val avgIntensity: String,
    val nsgLabel: String,
    val nsgUnit: String,
    val cgPositive: String,
    val cgNegative: String,
    val monthlyActivity: String,
    val topStormyDays: String,
    val polarityBreakdown: String,
    val interpretation: String,
    val normativeConclusion: String,
    val freeObservations: String,
    val generatedBy: String,
    val generatedOn: String,
    val network: String,
    val impacts: String,
    val rank: String,
    val count: String,
    val maxKa: String,
    val months: List<String>,
    val nsgConclusionText: (site: String, nsg: String, total: Int) -> String,
    val keraunicConclusionText: (
        site: String,
        total: Int,
        nsg: String,
        maxKa: Int,
        posCount: Int,
        negCount: Int
    ) -> String,
)

object PdfTranslations {

    fun get(lang: PdfOutputLang): PdfStrings = when (lang) {
        PdfOutputLang.FR -> fr
        PdfOutputLang.EN -> en
        PdfOutputLang.ES -> es
        PdfOutputLang.PT -> pt
    }

    private val fr = PdfStrings(
        docTitleKeraunic = "Rapport Kéraunique",
        docTitleNsg = "Certificat de Densité de Foudroiement (Nsg)",
        docTitleLightning = "Certificat de Foudroiement",
        site = "Site", latitude = "Latitude", longitude = "Longitude",
        period = "Période", from = "Du", to = "Au",
        totalStrikes = "Total impacts", maxIntensity = "Intensité max.",
        avgIntensity = "Intensité moy.", nsgLabel = "Densité Nsg",
        nsgUnit = "impacts/km²/an", cgPositive = "CG+ (positif)", cgNegative = "CG− (négatif)",
        monthlyActivity = "Activité mensuelle", topStormyDays = "Top 5 jours orageux",
        polarityBreakdown = "Répartition par polarité",
        interpretation = "INTERPRÉTATION", normativeConclusion = "Conclusion normative",
        freeObservations = "Observations", generatedBy = "Généré par Strike Radar",
        generatedOn = "Le", network = "Réseau GLD360+",
        impacts = "impacts", rank = "Rang", count = "Impacts", maxKa = "Max (kA)",
        months = listOf("Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"),
        nsgConclusionText = { site, nsg, total ->
            "Le site « $site » présente une densité de foudroiement sol (Nsg) de $nsg impacts/km²/an, " +
            "déterminée à partir de $total impacts enregistrés par le réseau GLD360+, " +
            "conformément à la norme IEC 62858:2019. " +
            "Cette valeur constitue une donnée d'entrée pour l'évaluation du risque foudre selon l'IEC 62305-2:2024."
        },
        keraunicConclusionText = { site, total, nsg, maxKa, posCount, negCount ->
            "Sur la période d'étude, le site « $site » enregistre $total impacts CG, " +
            "dont $posCount de polarité positive (CG+) et $negCount de polarité négative (CG−). " +
            "La densité Nsg est de $nsg impacts/km²/an (IEC 62858:2019). " +
            "L'intensité maximale relevée est de $maxKa kA. " +
            "Ces données sont les entrées de l'évaluation du risque foudre selon l'IEC 62305-2:2024."
        }
    )

    private val en = PdfStrings(
        docTitleKeraunic = "Keraunic report",
        docTitleNsg = "Ground Flash Density Certificate (Nsg)",
        docTitleLightning = "Lightning Strike Certificate",
        site = "Site", latitude = "Latitude", longitude = "Longitude",
        period = "Period", from = "From", to = "To",
        totalStrikes = "Total strikes", maxIntensity = "Max. intensity",
        avgIntensity = "Avg. intensity", nsgLabel = "Nsg density",
        nsgUnit = "strikes/km²/year", cgPositive = "CG+ (positive)", cgNegative = "CG− (negative)",
        monthlyActivity = "Monthly activity", topStormyDays = "Top 5 stormy days",
        polarityBreakdown = "Polarity breakdown",
        interpretation = "INTERPRETATION", normativeConclusion = "Normative conclusion",
        freeObservations = "Observations", generatedBy = "Generated by Strike Radar",
        generatedOn = "On", network = "GLD360+ network",
        impacts = "strikes", rank = "Rank", count = "Strikes", maxKa = "Max (kA)",
        months = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"),
        nsgConclusionText = { site, nsg, total ->
            "Site « $site » shows a ground flash density (Nsg) of $nsg strikes/km²/year, " +
            "determined from $total strikes recorded by the GLD360+ network, " +
            "in compliance with IEC 62858:2019. " +
            "This value is an input for lightning risk assessment per IEC 62305-2:2024."
        },
        keraunicConclusionText = { site, total, nsg, maxKa, posCount, negCount ->
            "Over the study period, site « $site » recorded $total CG strikes, " +
            "of which $posCount positive (CG+) and $negCount negative (CG−). " +
            "Ground flash density (Nsg): $nsg strikes/km²/year (IEC 62858:2019). " +
            "Maximum intensity: $maxKa kA. " +
            "These data are inputs for risk assessment per IEC 62305-2:2024."
        }
    )

    private val es = PdfStrings(
        docTitleKeraunic = "Informe ceráunico",
        docTitleNsg = "Certificado de Densidad de Descargas (Nsg)",
        docTitleLightning = "Certificado de Impacto de Rayo",
        site = "Sitio", latitude = "Latitud", longitude = "Longitud",
        period = "Período", from = "Desde", to = "Hasta",
        totalStrikes = "Total impactos", maxIntensity = "Intensidad máx.",
        avgIntensity = "Intensidad prom.", nsgLabel = "Densidad Nsg",
        nsgUnit = "impactos/km²/año", cgPositive = "CG+ (positivo)", cgNegative = "CG− (negativo)",
        monthlyActivity = "Actividad mensual", topStormyDays = "Top 5 días tormentosos",
        polarityBreakdown = "Distribución por polaridad",
        interpretation = "INTERPRETACIÓN", normativeConclusion = "Conclusión normativa",
        freeObservations = "Observaciones", generatedBy = "Generado por Strike Radar",
        generatedOn = "El", network = "Red GLD360+",
        impacts = "impactos", rank = "Orden", count = "Impactos", maxKa = "Máx (kA)",
        months = listOf("Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"),
        nsgConclusionText = { site, nsg, total ->
            "El sitio « $site » presenta una densidad Nsg de $nsg impactos/km²/año, " +
            "determinada a partir de $total impactos registrados por la red GLD360+, " +
            "conforme a la norma IEC 62858:2019. " +
            "Este valor es un dato de entrada para la evaluación del riesgo según IEC 62305-2:2024."
        },
        keraunicConclusionText = { site, total, nsg, maxKa, posCount, negCount ->
            "El análisis ceráunico del sitio « $site » registró $total impactos CG, " +
            "de los cuales $posCount positivos (CG+) y $negCount negativos (CG−). " +
            "Densidad Nsg: $nsg impactos/km²/año (IEC 62858:2019). " +
            "Intensidad máxima: $maxKa kA. " +
            "Datos de entrada para evaluación de riesgo IEC 62305-2:2024."
        }
    )

    private val pt = PdfStrings(
        docTitleKeraunic = "Relatório ceráunico",
        docTitleNsg = "Certificado de Densidade de Descargas (Nsg)",
        docTitleLightning = "Certificado de Impacto de Raio",
        site = "Local", latitude = "Latitude", longitude = "Longitude",
        period = "Período", from = "De", to = "Até",
        totalStrikes = "Total de impactos", maxIntensity = "Intensidade máx.",
        avgIntensity = "Intensidade méd.", nsgLabel = "Densidade Nsg",
        nsgUnit = "impactos/km²/ano", cgPositive = "CG+ (positivo)", cgNegative = "CG− (negativo)",
        monthlyActivity = "Atividade mensal", topStormyDays = "Top 5 dias de tempestade",
        polarityBreakdown = "Distribuição por polaridade",
        interpretation = "INTERPRETAÇÃO", normativeConclusion = "Conclusão normativa",
        freeObservations = "Observações", generatedBy = "Gerado pelo Strike Radar",
        generatedOn = "Em", network = "Rede GLD360+",
        impacts = "impactos", rank = "Posição", count = "Impactos", maxKa = "Máx (kA)",
        months = listOf("Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"),
        nsgConclusionText = { site, nsg, total ->
            "O local « $site » apresenta uma densidade Nsg de $nsg impactos/km²/ano, " +
            "determinada a partir de $total impactos registados pela rede GLD360+, " +
            "em conformidade com a norma IEC 62858:2019. " +
            "Este valor é uma entrada para a avaliação do risco segundo a IEC 62305-2:2024."
        },
        keraunicConclusionText = { site, total, nsg, maxKa, posCount, negCount ->
            "A análise ceráunica do local « $site » registou $total impactos CG, " +
            "dos quais $posCount positivos (CG+) e $negCount negativos (CG−). " +
            "Densidade Nsg: $nsg impactos/km²/ano (IEC 62858:2019). " +
            "Intensidade máxima: $maxKa kA. " +
            "Dados de entrada para avaliação de risco IEC 62305-2:2024."
        }
    )
}
